import { FormEvent, useEffect, useState } from "react";
import { Eye, RefreshCw, Search } from "lucide-react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "./AdminLayout";
import { AdminRefund, AdminUser, PaginationMeta, getCurrentAdmin, listAdminRefunds, logout } from "./api";
import Pagination from "./components/Pagination";
import { AdminTextTooltip } from "./components/AdminTextTooltip";

const defaultPagination: PaginationMeta = {
  Page: 1,
  PerPage: 20,
  Total: 0,
  TotalPages: 1,
};

/** 這個頁面是跨訂單的退款總覽（見 RefundApiController::index 說明），單一訂單的退款歷史與新增退款
 * 請到該訂單的訂單詳情頁操作（訂單詳情頁的「退款紀錄」區塊），這裡只查詢、不提供新增。 */
export default function AdminRefunds() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState<AdminUser | null>(null);
  const [refunds, setRefunds] = useState<AdminRefund[]>([]);
  const [summary, setSummary] = useState({ Count: 0, Total: 0 });
  const [pagination, setPagination] = useState<PaginationMeta>(defaultPagination);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [keyword, setKeyword] = useState("");
  const [loading, setLoading] = useState(true);
  const [pageMessage, setPageMessage] = useState("");

  async function load(filters: { StartDate?: string; EndDate?: string; Keyword?: string; Page?: number; PerPage?: number } = {}) {
    setLoading(true);
    setPageMessage("");
    try {
      const me = await getCurrentAdmin();
      setCurrentUser(me.User);
      const data = await listAdminRefunds({
        StartDate: filters.StartDate ?? startDate,
        EndDate: filters.EndDate ?? endDate,
        Keyword: filters.Keyword ?? keyword.trim(),
        Page: filters.Page ?? pagination.Page,
        PerPage: filters.PerPage ?? pagination.PerPage,
      });
      setRefunds(data.Refunds);
      setSummary(data.Summary || { Count: 0, Total: 0 });
      setPagination(data.Pagination || defaultPagination);
    } catch (error) {
      setPageMessage(error instanceof Error ? error.message : "退款資料載入失敗。");
      navigate("/admin-login", { replace: true });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load({ Page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleLogout() {
    await logout().catch(() => undefined);
    navigate("/admin-login", { replace: true });
  }

  function handleSearch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    load({ StartDate: startDate, EndDate: endDate, Keyword: keyword.trim(), Page: 1 });
  }

  function handleReset() {
    setStartDate("");
    setEndDate("");
    setKeyword("");
    load({ StartDate: "", EndDate: "", Keyword: "", Page: 1 });
  }

  return (
    <AdminLayout sectionLabel="帳務管理" title="退款管理" user={currentUser} onLogout={handleLogout}>
      <div className="mb-5 grid gap-3 sm:grid-cols-2">
        <div className="rounded-md border border-[#dfe6e1] bg-white p-4">
          <p className="text-xs text-[#7b877f]">篩選區間退款筆數</p>
          <p className="mt-1 text-2xl font-bold text-[#203d32]">{summary.Count.toLocaleString()} 筆</p>
        </div>
        <div className="rounded-md border border-[#dfe6e1] bg-white p-4">
          <p className="text-xs text-[#7b877f]">篩選區間退款總額</p>
          <p className="mt-1 text-2xl font-bold text-[#8a1f1f]">NT$ {summary.Total.toLocaleString()}</p>
        </div>
      </div>

      <form className="mb-5 grid gap-3 rounded-md border border-[#dfe6e1] bg-white p-3 md:flex md:flex-wrap md:items-end md:p-4" onSubmit={handleSearch}>
        <div className="grid gap-1">
          <label className="text-sm font-bold text-[#59665f]" htmlFor="refund-start">起始日期</label>
          <input id="refund-start" className="h-11 rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" type="date" value={startDate} onChange={(event) => setStartDate(event.target.value)} />
        </div>
        <div className="grid gap-1">
          <label className="text-sm font-bold text-[#59665f]" htmlFor="refund-end">結束日期</label>
          <input id="refund-end" className="h-11 rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" type="date" value={endDate} onChange={(event) => setEndDate(event.target.value)} />
        </div>
        <div className="grid min-w-0 gap-1 md:min-w-[260px] md:flex-1">
          <label className="text-sm font-bold text-[#59665f]" htmlFor="refund-keyword">關鍵字</label>
          <input id="refund-keyword" className="h-11 rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" placeholder="訂單編號、退款原因、操作人員" value={keyword} onChange={(event) => setKeyword(event.target.value)} />
        </div>
        <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white transition hover:bg-[#203d32]">
          <Search size={18} />
          搜尋
        </button>
        <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-5 font-bold text-[#315746] transition hover:bg-[#f0f5f2]" onClick={handleReset} type="button">
          <RefreshCw size={18} />
          清除條件
        </button>
      </form>

      {pageMessage && <div className="mb-4 rounded-md border border-[#f2b8b5] bg-[#fdebea] px-4 py-3 text-sm font-medium text-[#8a1f1f]">{pageMessage}</div>}

      <div className="rounded-md border border-[#dfe6e1] bg-white">
        <div className="max-h-[62vh] overflow-auto">
          <table className="w-full min-w-[900px] table-fixed border-collapse text-left">
            <thead className="sticky top-0 z-10 bg-[#f0f3f1] text-sm text-[#59665f] shadow-[inset_0_-1px_0_#dfe6e1]">
              <tr>
                <th className="w-[160px] px-4 py-3">時間</th>
                <th className="w-[200px] px-4 py-3">訂單編號</th>
                <th className="w-[140px] px-4 py-3">收件人</th>
                <th className="w-[140px] px-4 py-3">退款金額</th>
                <th className="px-4 py-3">原因</th>
                <th className="w-[120px] px-4 py-3">操作人員</th>
                <th className="w-[90px] px-4 py-3">操作</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td className="px-4 py-10 text-center text-[#59665f]" colSpan={7}>載入中...</td></tr>
              ) : refunds.length === 0 ? (
                <tr><td className="px-4 py-10 text-center text-[#59665f]" colSpan={7}>沒有符合條件的退款紀錄。</td></tr>
              ) : refunds.map((refund) => (
                <tr className="border-t border-[#edf0ee]" key={refund.RefundId}>
                  <td className="px-4 py-4 text-[#59665f]">{refund.CreatedAt || "-"}</td>
                  <td className="px-4 py-4 font-bold text-[#203d32]">{refund.OrderNumber || "-"}</td>
                  <td className="px-4 py-4 text-[#59665f]">{refund.RecipientName || "-"}</td>
                  <td className="px-4 py-4 font-bold text-[#8a1f1f]">-NT$ {refund.Amount.toLocaleString()}</td>
                  <td className="max-w-0 px-4 py-4 text-[#59665f]"><AdminTextTooltip text={refund.Reason} /></td>
                  <td className="px-4 py-4 text-[#59665f]">{refund.OperatorName || "-"}</td>
                  <td className="px-4 py-4">
                    <button className="inline-flex h-9 items-center gap-1 rounded-md border border-[#cfd8d2] bg-white px-3 text-sm font-bold text-[#315746] hover:bg-[#f0f5f2]" onClick={() => navigate(`/admin/orders/${refund.OrderId}`)} type="button">
                      <Eye size={16} />查看
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination disabled={loading} pagination={pagination} onChange={(page) => load({ Page: page })} />
      </div>
    </AdminLayout>
  );
}
